import { ArrowLeft } from "lucide-react";
import { useState } from "react";

import { useChooseEntityConfig } from "../../choose-entity-config-context";
import { hasReleaseLabels } from "../utils";

import { EntitySelection, useChooseEntityVariant } from "./choose-entity-variant-context";
import LabelVersionView from "./LabelVersionView";
import VersionContent from "./VersionContent";

import { Button } from "@/components/ui/button";
import { DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EntityIcon } from "@/components/ui/icons";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { getItemColor } from "@/components/UnifiedRegistry/utils";
import { Entity } from "@/queries";

const ChooseEntityVariantView = () => {
  const { entity, readonly, multiSelect, selectedEntities, onSelect, onOpenChange, onBack } =
    useChooseEntityVariant();

  const config = useChooseEntityConfig();
  const allowDuplicateVariants = config ? config.allowDuplicateVariants : false;

  const entityType = entity.type;
  const currentVersionNumber = entity.metadata?.latest_version_number;

  const isLoading = false;
  const latestVersion = currentVersionNumber;
  const hasNoVersions = !latestVersion || latestVersion < 1;
  const [selectedVersion, setSelectedVersion] = useState<number | undefined | null>(null);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("versions");

  const handleVersionSelect = (version?: number) => {
    setSelectedVersion(version);
  };

  const handleLabelSelect = (label: string) => {
    setSelectedLabel(label);
  };

  const handleConfirm = () => {
    if (selectedLabel !== null) {
      const selection: EntitySelection = {
        type: "label",
        label: selectedLabel,
      };
      onSelect(selection);
      if (!multiSelect) {
        onOpenChange(false);
      }
      return;
    }

    if (selectedVersion === null) {
      return;
    }

    const selection: EntitySelection = {
      type: "version",
      version: selectedVersion,
    };
    onSelect(selection);
    if (!multiSelect) {
      onOpenChange(false);
    }
  };

  // Check if selecting "All Versions" should remove other variants
  const shouldReplaceWithAllVersions = (): boolean =>
    allowDuplicateVariants && activeTab === "versions" && selectedVersion === undefined;

  // Check if the currently selected variant is already in the selection
  const isVariantAlreadySelected = (): boolean => {
    if (!allowDuplicateVariants && (!config || (config && config.selectLatest))) {
      return false;
    }

    // "All Versions" should never be disabled - it replaces other selections
    if (shouldReplaceWithAllVersions()) {
      return false;
    }

    if (activeTab === "labels") {
      if (selectedLabel === null) {
        return false;
      }
      return selectedEntities.some(
        (e: Entity) => e.id === entity.id && e.type === entity.type && e.label === selectedLabel,
      );
    }

    if (selectedVersion === null) {
      return false;
    }

    return selectedEntities.some(
      (e: Entity) =>
        e.id === entity.id && e.type === entity.type && e.version_number === selectedVersion,
    );
  };

  const showLabelsAndVersions = hasReleaseLabels(entity);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSelectedVersion(null);
    setSelectedLabel(null);
  };

  const title = showLabelsAndVersions ? "Which version or release label?" : "Which version?";
  const description = showLabelsAndVersions
    ? "Pick the version or release label you want to use."
    : "Pick the version you want to use.";

  return (
    <div className="flex h-full min-h-0 flex-col">
      <DialogHeader className="space-y-2 border-b border-gray-200 px-6 py-4">
        <DialogTitle className="flex items-center gap-3 text-lg font-semibold text-gray-900">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            aria-label="Back"
            className="h-10 w-10 flex-shrink-0 p-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div
            className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${getItemColor(
              entityType,
            )}`}
          >
            <EntityIcon type={entity.type} size="md" />
          </div>
          <span className="min-w-0 flex-1 truncate">{title}</span>
        </DialogTitle>
        <DialogDescription className="pl-[52px] text-sm text-gray-600">
          {description}
        </DialogDescription>
        <div className="pl-[52px]">
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <OverflowDisplayTooltip content={entity.name} className="min-w-0">
              <span className="block truncate">{entity.name}</span>
            </OverflowDisplayTooltip>
          </div>
        </div>
      </DialogHeader>

      <div className="min-h-0 flex-1 overflow-hidden">
        {showLabelsAndVersions ? (
          <LabelVersionView
            latestVersion={latestVersion}
            readonly={readonly}
            onVersionSelect={handleVersionSelect}
            onLabelSelect={handleLabelSelect}
            onTabChange={handleTabChange}
          />
        ) : (
          <VersionContent
            isLoading={isLoading}
            latestVersion={latestVersion}
            selectedVersion={selectedVersion}
            readonly={readonly}
            onVersionSelect={handleVersionSelect}
          />
        )}
      </div>

      <div className="flex justify-end gap-2 border-t border-gray-200 px-6 py-4">
        <Button variant="ghost" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={
            readonly ||
            (activeTab === "labels" && selectedLabel === null) ||
            isVariantAlreadySelected() ||
            (activeTab === "versions" && (hasNoVersions || selectedVersion === null))
          }
        >
          {multiSelect ? "Next" : "Confirm"}
        </Button>
      </div>
    </div>
  );
};

export { ChooseEntityVariantProvider } from "./choose-entity-variant-context";
export default ChooseEntityVariantView;
